/**
 * Freeze V2 — Setup Tab
 *
 * Single unified freeze-settings form (replaces v1's separate FreezeToolbar +
 * FreezeEditBar). Shared fields (storage, grader, freeze tech, freezer) are
 * single-sourced from batchForm. The form drives two actions:
 *   - Update Embryos — patches the currently selected embryos immediately.
 *   - Update OvaDrive (action bar) — pushes the same batch settings on finalize.
 * Bulk-only fields (freeze method, account, deactivate/CZ/LG) apply only to the
 * Update Embryos patch.
 */
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { Check, Loader2 } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { useGetPredictionTechsQuery } from '@/app/services/equipment';
import { useGetFreezeStorageLocationsQuery } from '@/app/services/locations';
import { useGetOptionSetsByNameQuery } from '@/app/services/lookups';
import { addToast } from '@/app/slices/toast';
import useDebounce from '@/components/hooks/useDebounce';
import { useMediaQuery } from '@/components/hooks/useMediaQuery';
import { SearchableSelect } from '@/components/common/searchable-select';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '@/components/ui/combobox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SELECTION_LIMIT } from '@/constants';
import { AccountResponse, useSearchAccountsLazy } from '@/services/accounts';
import { getFertilizationSiteId } from '@/utils/freezeSites';

import { useFreezeForm, useFreezeSelection } from '../../contexts';
import {
  useUpdateFreezeEmbryosMutation,
  useUpdateFreezeTempEmbryosMutation,
} from '../../services/freezeApi';
import { FREEZE_DAY_OPTIONS, MOBILE_BREAKPOINT } from '../../utils/constants';

function selectLabel(
  options: readonly { label: string; value: string | number }[],
  placeholder: ReactNode,
) {
  return (value: string | number | null) => {
    const match = options.find((o) => o.value === value);
    if (match) return match.label;
    return <span className="text-muted-foreground">{placeholder}</span>;
  };
}

export function SetupTab({ onUpdated }: { onUpdated?: () => void }) {
  const dispatch = useDispatch();
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);
  const { batchForm, dispatch: formDispatch } = useFreezeForm();
  const selection = useFreezeSelection();

  const [params] = useSearchParams();
  const siteId = params.get('sId');

  const { data: techsData } = useGetPredictionTechsQuery({
    where: {
      or: [{ farmlocation: { eq: false } }, { farmlocation: { eq: null } }],
    },
  });
  const {
    data: storageLocations,
    isLoading: isStorageLoading,
    isFetching: isStorageFetching,
  } = useGetFreezeStorageLocationsQuery(getFertilizationSiteId(siteId), {
    skip: !siteId,
  });
  const isStorageBusy = isStorageLoading || isStorageFetching;
  const { data: freezeMethods } =
    useGetOptionSetsByNameQuery('Freezing Methods');

  const techOptions = useMemo(
    () =>
      (techsData ?? []).map((tech) => ({
        label: tech.name ?? '',
        value: tech.id ?? '',
      })),
    [techsData],
  );

  const storageOptions = useMemo(
    () =>
      (storageLocations ?? []).map((loc) => ({
        label: loc.name ?? '',
        value: loc.id ?? '',
      })),
    [storageLocations],
  );

  const freezeMethodOptions = useMemo(
    () =>
      (freezeMethods ?? []).map((item) => ({
        label: item.label ?? '',
        value: item.id ?? '',
      })),
    [freezeMethods],
  );

  const setBatchField = useCallback(
    (field: string, value: unknown) => {
      formDispatch({ type: 'SET_BATCH', field: field as any, value });
    },
    [formDispatch],
  );

  // Bulk-only fields — applied to the selected embryos on Update Embryos.
  const [editFreezeMethod, setEditFreezeMethod] = useState('');
  const [editDeactivate, setEditDeactivate] = useState(false);
  const [editCrackedZona, setEditCrackedZona] = useState(false);
  const [editLowQuality, setEditLowQuality] = useState(false);
  const [editAccount, setEditAccount] = useState<AccountResponse | null>(null);
  const [accountSuggestions, setAccountSuggestions] = useState<
    AccountResponse[]
  >([]);
  const [accountQuery, setAccountQuery] = useState('');
  const debouncedAccountQuery = useDebounce(accountQuery);
  const [searchAccounts] = useSearchAccountsLazy();

  useEffect(() => {
    let cancelled = false;
    searchAccounts({
      search: debouncedAccountQuery,
      selectionLimit: SELECTION_LIMIT,
    })
      .then(({ data = [] }) => {
        if (!cancelled) setAccountSuggestions(data);
      })
      .catch(() => {
        if (!cancelled) setAccountSuggestions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedAccountQuery, searchAccounts]);

  const { mutateAsync: updateEmbryos, isPending: isUpdatingLive } =
    useUpdateFreezeEmbryosMutation();
  const { mutateAsync: updateTempEmbryos, isPending: isUpdatingTemp } =
    useUpdateFreezeTempEmbryosMutation();
  const isBulkUpdating = isUpdatingLive || isUpdatingTemp;

  const hasSelection =
    selection.liveEmbryoIds.length > 0 || selection.tempEmbryoIds.length > 0;

  const handleBulkUpdate = useCallback(async () => {
    const { liveEmbryoIds, tempEmbryoIds } = selection;

    if (!liveEmbryoIds.length && !tempEmbryoIds.length) {
      dispatch(
        addToast({
          severity: 'error',
          summary: 'No Selection',
          detail: 'Select embryos before applying bulk edits.',
          life: 3000,
        }),
      );
      return;
    }

    try {
      if (liveEmbryoIds.length) {
        await updateEmbryos({
          embryoIds: liveEmbryoIds,
          storageLocationId: batchForm.storageLocationId || null,
          freezeMethodId: editFreezeMethod || null,
          graderId: batchForm.graderId || null,
          freezeTechId: batchForm.freezeTechId || null,
          accountId: editAccount?.id ?? null,
          deactivate: editDeactivate || null,
          crackedZona: editCrackedZona || null,
          lowQuality: editLowQuality || null,
          freezer: batchForm.freezer || null,
        });
      }

      if (tempEmbryoIds.length) {
        await updateTempEmbryos({
          tempEmbryoIds,
          storageLocationId: batchForm.storageLocationId || null,
          freezeMethodId: editFreezeMethod || null,
          freezeTechId: batchForm.freezeTechId || null,
          accountId: editAccount?.id ?? null,
          deactivate: editDeactivate || null,
          crackedZona: editCrackedZona || null,
          lowQuality: editLowQuality || null,
          freezer: batchForm.freezer || null,
        });
      }

      dispatch(
        addToast({
          severity: 'success',
          summary: 'Updated',
          detail: `Updated ${liveEmbryoIds.length + tempEmbryoIds.length} embryos.`,
          life: 3000,
        }),
      );
      onUpdated?.();
    } catch (error) {
      dispatch(
        addToast({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to update embryos.',
          life: 3000,
        }),
      );
    }
  }, [
    selection,
    batchForm,
    editFreezeMethod,
    editAccount,
    editDeactivate,
    editCrackedZona,
    editLowQuality,
    updateEmbryos,
    updateTempEmbryos,
    dispatch,
    onUpdated,
  ]);

  const gridClasses = isMobile
    ? 'grid grid-cols-1 gap-2.5'
    : 'grid grid-cols-2 gap-x-4 gap-y-2 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6';

  return (
    <div className="flex flex-col gap-3">
      <div className={gridClasses}>
        <div className="flex flex-col gap-2">
          <Label className="text-sm">Storage Location</Label>
          <SearchableSelect
            className="h-8 w-full text-sm"
            disabled={!siteId}
            options={storageOptions}
            value={batchForm.storageLocationId ?? ''}
            onChange={(v) => {
              setBatchField('storageLocationId', v || null);
              // Freeze location (emb.siteId) is derived from the storage location's site, matching v1.
              const loc = storageLocations?.find((l) => l.id === v) as
                | { siteId?: string }
                | undefined;
              setBatchField('freezeLocation', loc?.siteId ?? null);
            }}
            placeholder={siteId ? 'Select location' : 'Select a site first'}
            emptyMessage={
              !siteId
                ? 'Select a fertilization site to load storage locations.'
                : isStorageBusy
                  ? 'Loading storage locations…'
                  : 'No storage locations found for this site.'
            }
          />
        </div>

        <div className="flex flex-col gap-2">
          <Label className="text-sm">Freeze Day</Label>
          <Select
            value={batchForm.freezeDay ?? ''}
            onChange={(v) =>
              setBatchField('freezeDay', v == null ? null : String(v))
            }
          >
            <SelectTrigger className="h-8 w-full text-sm">
              <SelectValue>
                {selectLabel(FREEZE_DAY_OPTIONS, 'Select day')}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {FREEZE_DAY_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-2">
          <Label className="text-sm">Prediction Tech</Label>
          <SearchableSelect
            className="h-8 w-full text-sm"
            options={techOptions}
            value={batchForm.predictionTechId ?? ''}
            onChange={(v) => setBatchField('predictionTechId', v || null)}
            placeholder="Select tech"
            emptyMessage="No technicians found"
          />
        </div>

        <div className="flex flex-col gap-2">
          <Label className="text-sm">Grader</Label>
          <SearchableSelect
            className="h-8 w-full text-sm"
            options={techOptions}
            value={batchForm.graderId ?? ''}
            onChange={(v) => setBatchField('graderId', v || null)}
            placeholder="Select grader"
            emptyMessage="No technicians found"
          />
        </div>

        <div className="flex flex-col gap-2">
          <Label className="text-sm">Freeze Tech</Label>
          <SearchableSelect
            className="h-8 w-full text-sm"
            options={techOptions}
            value={batchForm.freezeTechId ?? ''}
            onChange={(v) => setBatchField('freezeTechId', v || null)}
            placeholder="Select tech"
            emptyMessage="No technicians found"
          />
        </div>

        <div className="flex flex-col gap-2">
          <Label className="text-sm">Freezer</Label>
          <Input
            className="h-8 text-sm"
            value={batchForm.freezer}
            onChange={(e) => setBatchField('freezer', e.target.value)}
            placeholder="Enter freezer"
          />
        </div>

        <div className="flex flex-col gap-2">
          <Label className="text-sm">Freeze Method</Label>
          <Select value={editFreezeMethod} onChange={setEditFreezeMethod}>
            <SelectTrigger className="h-8 w-full text-sm">
              <SelectValue>
                {selectLabel(freezeMethodOptions, 'Select method')}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {freezeMethodOptions.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-2">
          <Label className="text-sm">Account</Label>
          <Combobox<AccountResponse>
            items={accountSuggestions}
            value={editAccount}
            onValueChange={(account) => {
              setEditAccount(account);
              setAccountQuery('');
            }}
            onInputValueChange={setAccountQuery}
            itemToStringValue={(a) => a.id}
            itemToStringLabel={(a) => `${a.name ?? ''} [${a.code ?? ''}]`}
          >
            <ComboboxInput
              className="h-8 text-sm"
              placeholder="Search account"
              showClear
            />
            <ComboboxContent>
              <ComboboxEmpty>No accounts found</ComboboxEmpty>
              <ComboboxList>
                {(option: AccountResponse) => (
                  <ComboboxItem key={option.id} value={option}>
                    {option.name} [{option.code}]
                  </ComboboxItem>
                )}
              </ComboboxList>
            </ComboboxContent>
          </Combobox>
        </div>

        <div className="flex flex-col gap-2">
          <Label className="text-sm">Embs/Vit Straw</Label>
          <Input
            type="number"
            className="h-8 text-sm"
            value={batchForm.embryosPerVitStraw ?? ''}
            onChange={(e) =>
              setBatchField(
                'embryosPerVitStraw',
                e.target.value ? Number(e.target.value) : null,
              )
            }
            min={0}
          />
        </div>

        <div className="flex flex-col gap-2">
          <Label className="text-sm">Embs/Straw</Label>
          <Input
            type="number"
            className="h-8 text-sm"
            value={batchForm.embryosPerStraw ?? ''}
            onChange={(e) =>
              setBatchField(
                'embryosPerStraw',
                e.target.value ? Number(e.target.value) : null,
              )
            }
            min={0}
          />
        </div>

        <div className="col-span-2 flex flex-wrap items-center gap-x-4 gap-y-2 pt-1 sm:pt-5">
          <div className="flex items-center gap-2">
            <Checkbox
              id="dtPlus"
              checked={batchForm.dtPlus}
              onCheckedChange={(checked) => setBatchField('dtPlus', !!checked)}
            />
            <Label htmlFor="dtPlus" className="text-xs">
              DT+
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="outsideFreeze"
              checked={batchForm.outsideFreeze}
              onCheckedChange={(checked) =>
                setBatchField('outsideFreeze', !!checked)
              }
            />
            <Label htmlFor="outsideFreeze" className="text-xs">
              Outside Freeze
            </Label>
          </div>
          <div className="flex items-center gap-1.5">
            <Checkbox
              id="editDeactivate"
              checked={editDeactivate}
              onCheckedChange={(c) => setEditDeactivate(!!c)}
            />
            <Label htmlFor="editDeactivate" className="text-xs">
              Deactivate
            </Label>
          </div>
          <div className="flex items-center gap-1.5">
            <Checkbox
              id="editCZ"
              checked={editCrackedZona}
              onCheckedChange={(c) => setEditCrackedZona(!!c)}
            />
            <Label htmlFor="editCZ" className="text-xs">
              CZ
            </Label>
          </div>
          <div className="flex items-center gap-1.5">
            <Checkbox
              id="editLG"
              checked={editLowQuality}
              onCheckedChange={(c) => setEditLowQuality(!!c)}
            />
            <Label htmlFor="editLG" className="text-xs">
              LG
            </Label>
          </div>
        </div>
      </div>

      <div className="mt-1 flex flex-col items-end gap-1.5 sm:flex-row sm:items-center sm:justify-end">
        <span className="text-muted-foreground text-xs">
          Update Embryos applies these settings to the selected embryos now.
          Update OvaDrive (top) converts completed temp embryos into live
          embryos and applies batch information — Setup fields only fill empty
          values, while QC fields are always updated.
        </span>
        <Button
          size="sm"
          onClick={handleBulkUpdate}
          disabled={isBulkUpdating || !hasSelection}
          title={
            !hasSelection ? 'Select one or more embryos to update' : undefined
          }
        >
          {isBulkUpdating ? (
            <Loader2 className="mr-1.5 size-3.5 animate-spin" />
          ) : (
            <Check className="mr-1.5 size-3.5" />
          )}
          {isBulkUpdating ? 'Updating…' : 'Update Embryos'}
        </Button>
      </div>
    </div>
  );
}
